import { useProjectListStore } from '../../store/projectListStore';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';
import { createProject, deleteProject, getProjectList, reorderProject, getProjectListForOwner, updateProjectListType, toggleProjectAccess } from '../../api/projectApi';
import { ProjectSimpleData } from '../../dto/EntityRepository';
import { CreateProjectReq, RemoveProjectReq, UpdateProjectReq, UpdateReorderListReq, UpdateProjectListTypeReq } from '../../dto/ReqDtoRepository';
import { CreatedProjectResponse, ProjectListResponse, ProjectListWithPropertyResponse, ProjectResponse, ProjectTypeResponse } from '../../dto/ResDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { ProjectListType } from '../../enum/EnumRepository';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';


interface UseProjectListStateResult {
  projects: ProjectSimpleData[];
  projectListType: ProjectListType | null;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseProjectListActionsResult {
  getProjectList: () => Promise<void>;
  getAllProjectsForOwner: () => Promise<void>;
  createProject: (data: CreateProjectReq) => Promise<void>;
  updateProjectListType: (data: UpdateProjectListTypeReq) => Promise<void>;
  toggleProjectAccess: (data: UpdateProjectReq) => Promise<void>;
  deleteProject: (data: RemoveProjectReq) => Promise<void>;
  reorderProject: (data: UpdateReorderListReq) => Promise<void>;
}

export const useProjectListState = (): UseProjectListStateResult => {
  const projects = useProjectListStore((state) => state.projects);
  const projectListType = useProjectListStore((state) => state.projectListType);
  const listOwnerAui = useProjectListStore((state) => state.listOwnerAui);
  const listScope = useProjectListStore((state) => state.listScope);
  const status = useProjectListStore((state) => state.status);

  return { projects, projectListType, listOwnerAui, listScope, status };
};

export const useProjectListActions = (): UseProjectListActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const setProjects = useProjectListStore((state) => state.setProjects);
  const afterCreateProject = useProjectListStore((state) => state.afterCreateProject);
  const beginProjectListFetch = useProjectListStore((state) => state.beginProjectListFetch);
  const setProjectListAndType = useProjectListStore((state) => state.setProjectListAndType);
  const setProjectListError = useProjectListStore((state) => state.setProjectListError);
  const setProjectListType = useProjectListStore((state) => state.setProjectListType);
  const setProjectAccessAfterUpdate = useProjectListStore((state) => state.setProjectAccessAfterUpdate);
  const afterDeleteProject = useProjectListStore((state) => state.afterDeleteProject);
  const setDeletedTempAlert = useTempAlertStore((state) => state.setDeletedTempAlert);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();

  const handleProjectListSuccess = (
    response: ProjectListWithPropertyResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    const projectData = response.data;

    setProjectListAndType(
      projectData.projectList,
      projectData.projectListType,
      ownerAui,
      scope,
    );
  };

  const handleDeleteProjectSuccess = (projectId: string) => {
    afterDeleteProject(projectId);
    console.log("deleted well");
    setDeletedTempAlert();
  };

  const handleToggleProjectAccessSuccess = (response: ProjectResponse) => {
    const projectData = response.data;
    setProjectAccessAfterUpdate(projectData);
    setUpdatedTempAlert();
  };

  const handleCreateProjectSuccess = (response: CreatedProjectResponse) => {
    const createdProjectData = response.data;
    afterCreateProject(createdProjectData);
  };

  const handleReorderProjectSuccess = (response: ProjectListResponse) => {
    const projectListData = response.data;
    setProjects(projectListData);
    setUpdatedTempAlert();
  };

  const handleUpdateProjectListTypeSuccess = (response: ProjectTypeResponse) => {
    const data = response.data;
    const projectListType = data.projectListType;
    setProjectListType(projectListType);
  };

  const fetchProjectList = async (aui: string, scope: ResourceScope) => {
    beginProjectListFetch(aui, scope);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => (scope === 'owner' ? getProjectListForOwner(aui) : getProjectList(aui)),
        (response) => handleProjectListSuccess(response, aui, scope),
      );
    } catch (error) {
      setProjectListError();
      throw error;
    }
  };

  const handleProjectRequest = async (
    action: 'get' | 'get_for_owner' | 'create' | 'update_project_list_type' | 'toggle_access' | 'delete' | 'reorder',
    data?: CreateProjectReq | RemoveProjectReq | UpdateReorderListReq | UpdateProjectListTypeReq | UpdateProjectReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'delete': {
          const projectId = (data as RemoveProjectReq).projectId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteProject(aui, data as RemoveProjectReq),
            () => handleDeleteProjectSuccess(projectId),
          );
        }
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createProject(aui, data as CreateProjectReq),
            handleCreateProjectSuccess,
          );
        case 'update_project_list_type':
          return runCurrentArchiveRequest(
            aui,
            () => updateProjectListType(aui, data as UpdateProjectListTypeReq),
            handleUpdateProjectListTypeSuccess,
          );
        case 'toggle_access':
          return runCurrentArchiveRequest(
            aui,
            () => toggleProjectAccess(aui, data as UpdateProjectReq),
            handleToggleProjectAccessSuccess,
          );
        case 'reorder':
          return runCurrentArchiveRequest(
            aui,
            () => reorderProject(aui, data as UpdateReorderListReq),
            handleReorderProjectSuccess,
          );
        case 'get_for_owner':
          return fetchProjectList(aui, 'owner');
        case 'get':
        default:
          return fetchProjectList(aui, 'public');
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getProjectListHandler = () => handleProjectRequest('get');
  const getAllProjectsForOwnerHandler = () => handleProjectRequest('get_for_owner');
  const createProjectHandler = (data: CreateProjectReq) => handleProjectRequest('create', data);
  const updateProjectListTypeHandler = (data: UpdateProjectListTypeReq) => handleProjectRequest('update_project_list_type', data);
  const toggleProjectAccessHandler = (data: UpdateProjectReq) => handleProjectRequest('toggle_access', data);
  const removeProjectHandler = (data: RemoveProjectReq) => handleProjectRequest('delete', data);
  const reorderProjectHandler = (data: UpdateReorderListReq) => handleProjectRequest('reorder', data);

  return {
    getProjectList: getProjectListHandler,
    getAllProjectsForOwner: getAllProjectsForOwnerHandler,
    createProject: createProjectHandler,
    updateProjectListType: updateProjectListTypeHandler,
    toggleProjectAccess: toggleProjectAccessHandler,
    deleteProject: removeProjectHandler,
    reorderProject: reorderProjectHandler
  };
};
